import { useState, useEffect } from 'react';

// ----------------------------------------------------------------------

type Dimensions = { width: number; height: number };

// Accept both RefObject<T | null> and MutableRefObject<T | undefined>
type AnyRef<T> = { current: T | null | undefined };

// Drop-in replacement for the abandoned react-hooks-get-dimensions package.
// Observes the referenced element via ResizeObserver and reports its
// offsetWidth/offsetHeight; falls back to window dimensions when no
// element ref is provided.
export function useGetDimensions<T extends HTMLElement = HTMLElement>(ref?: AnyRef<T>): Dimensions {
  const [dimensions, setDimensions] = useState<Dimensions>(() => {
    if (typeof window === 'undefined') return { width: 0, height: 0 };
    return ref?.current
      ? { width: ref.current.offsetWidth, height: ref.current.offsetHeight }
      : { width: window.innerWidth, height: window.innerHeight };
  });

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;

    if (!ref?.current) {
      const updateWindow = () =>
        setDimensions({ width: window.innerWidth, height: window.innerHeight });
      updateWindow();
      window.addEventListener('resize', updateWindow);
      return () => window.removeEventListener('resize', updateWindow);
    }

    const el = ref.current;
    const updateElement = () => setDimensions({ width: el.offsetWidth, height: el.offsetHeight });
    updateElement();

    const observer = new ResizeObserver(updateElement);
    observer.observe(el);
    return () => observer.disconnect();
  }, [ref]);

  return dimensions;
}
